export interface UserIdentity {
  uid?: string | null;
  email?: string | null;
  mail?: string | null;
}

const normalize = (value?: string | null): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const emailOf = (user: UserIdentity): string =>
  normalize(user.email) || normalize(user.mail);

/**
 * True when both refer to the same person: uid decides when both have one,
 * otherwise email/mail. Names are never compared (they are not unique).
 */
export function isSameUser(
  a?: UserIdentity | null,
  b?: UserIdentity | null
): boolean {
  if (!a || !b) return false;
  const uidA = normalize(a.uid);
  const uidB = normalize(b.uid);
  if (uidA && uidB) return uidA === uidB;
  const emailA = emailOf(a);
  const emailB = emailOf(b);
  return Boolean(emailA && emailB && emailA === emailB);
}

/** True when `user` matches any entry of `excluded`. */
export function isExcludedUser(
  user: UserIdentity | null | undefined,
  excluded?: Array<UserIdentity | null | undefined> | null
): boolean {
  if (!user || !excluded || excluded.length === 0) return false;
  return excluded.some(entry => isSameUser(user, entry));
}

/** `list` without anyone matching an entry of `excluded`; same list if none given. */
export function excludeUsers<T extends UserIdentity>(
  list: T[] | null | undefined,
  excluded?: Array<UserIdentity | null | undefined> | null
): T[] {
  if (!list) return [];
  if (!excluded || excluded.length === 0) return list;
  return list.filter(item => !isExcludedUser(item, excluded));
}

/** `list` without `user`; a missing/empty `user` excludes nothing. */
export function excludeUser<T extends UserIdentity>(
  list: T[] | null | undefined,
  user?: UserIdentity | null
): T[] {
  return excludeUsers(list, [user]);
}

/** Default approvers with the requestor (logged-in user) removed. */
export function stripRequestorFromDefaults<T extends UserIdentity>(
  defaults: T[] | null | undefined,
  requestor?: UserIdentity | null
): T[] {
  return excludeUser(defaults, requestor);
}
